// src/contexts/LiveKitRoomContext.tsx

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
  ConnectionState, // ✅ Import ConnectionState
  Participant,
  Room,
  RoomEvent,
} from 'livekit-client';

interface ILiveKitRoomContext {
  room: Room | undefined;
  setRoom: (room: Room | undefined) => void;
  participants: Participant[];
}

const LiveKitRoomContext = createContext<ILiveKitRoomContext | undefined>(
  undefined,
);

export const LiveKitRoomProvider = ({ children }: { children: ReactNode }) => {
  const [room, setRoom] = useState<Room | undefined>(undefined);
  const [participants, setParticipants] = useState<Participant[]>([]);

  useEffect(() => {
    if (!room) return;

    const updateParticipants = () => {
      setParticipants([
        room.localParticipant,
        ...Array.from(room.remoteParticipants.values()),
      ]);
    };

    // ✅ This function will run when the room is fully connected
    const onConnected = () => {
      updateParticipants(); // Now we can safely get the initial participants
    };

    // Set up listeners for all relevant events
    room.on(RoomEvent.ParticipantConnected, updateParticipants);
    room.on(RoomEvent.ParticipantDisconnected, updateParticipants);
    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      if (state === ConnectionState.Connected) {
        onConnected();
      }
    });

    if (room.state === ConnectionState.Connected) {
      onConnected();
    }

    // Cleanup all listeners
    return () => {
      room.removeAllListeners();
    };
  }, [room]); // We only need to re-run this entire setup when the room object changes

  const value = { room, setRoom, participants };

  return (
    <LiveKitRoomContext.Provider value={value}>
      {children}
    </LiveKitRoomContext.Provider>
  );
};

// Custom hook for easy access
export const useLiveKitRoom = () => {
  const context = useContext(LiveKitRoomContext);
  if (!context) {
    throw new Error('useLiveKitRoom must be used within a LiveKitRoomProvider');
  }
  return context;
};
